import React, { useEffect, useState } from 'react';
import { Globe, ShoppingBag, Info, Save, AlertTriangle, Check, X } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const BRAND_BROWN = '#5D4E3C';
const BRAND_TAN = '#C4A084';

const FLAGS = { DE: '🇩🇪', AT: '🇦🇹', FR: '🇫🇷' };

function ZoneCard({ zone, onSave }) {
  const [draft, setDraft] = useState(zone);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(zone);
    setDirty(false);
  }, [zone]);

  const update = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const {
        country_name, currency, vat_rate, shipping_cost, free_shipping_threshold,
        delivery_days_min, delivery_days_max, is_active, requires_customs,
        tax_note, legal_note, priority,
      } = draft;
      const payload = {
        country_name, currency, vat_rate: parseFloat(vat_rate), shipping_cost: parseFloat(shipping_cost),
        free_shipping_threshold: free_shipping_threshold === '' || free_shipping_threshold === null ? null : parseFloat(free_shipping_threshold),
        delivery_days_min: parseInt(delivery_days_min, 10),
        delivery_days_max: parseInt(delivery_days_max, 10),
        is_active, requires_customs, tax_note, legal_note,
        priority: parseInt(priority, 10),
      };
      await axiosInstance.patch(`/admin/shipping-zones/${zone.country_code}`, payload);
      toast.success(`${zone.country_name} gespeichert.`);
      setDirty(false);
      onSave && onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const flag = FLAGS[zone.country_code] || '🏳️';

  return (
    <Card
      className="rounded-none border-l-[3px]"
      style={{ borderLeftColor: draft.is_active ? '#7BA98E' : '#D89B7B' }}
      data-testid={`shipping-zone-${zone.country_code}`}
    >
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{flag}</span>
            <div>
              <div className="text-lg font-medium" style={{ color: BRAND_BROWN }}>
                {draft.country_name}
              </div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: '#8B7355' }}>
                {draft.country_code} · {draft.is_eu ? 'EU' : 'Nicht-EU'}
                {draft.requires_customs && ' · Zoll erforderlich'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: '#5D4E3C' }}>
              {draft.is_active ? 'Aktiv' : 'Inaktiv'}
            </span>
            <Switch
              checked={!!draft.is_active}
              onCheckedChange={(v) => update({ is_active: v })}
              data-testid={`toggle-active-${zone.country_code}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: BRAND_BROWN }}>Versandkosten</label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="0.10"
                value={draft.shipping_cost}
                onChange={(e) => update({ shipping_cost: e.target.value })}
                className="rounded-none"
                data-testid={`input-shipping-${zone.country_code}`}
              />
              <span className="text-xs" style={{ color: '#6B5D4F' }}>{draft.currency}</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: BRAND_BROWN }}>Kostenlos ab</label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="1"
                placeholder="—"
                value={draft.free_shipping_threshold ?? ''}
                onChange={(e) => update({ free_shipping_threshold: e.target.value === '' ? null : e.target.value })}
                className="rounded-none"
              />
              <span className="text-xs" style={{ color: '#6B5D4F' }}>{draft.currency}</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: BRAND_BROWN }}>MwSt-Satz</label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="0.001"
                value={draft.vat_rate}
                onChange={(e) => update({ vat_rate: e.target.value })}
                className="rounded-none"
              />
              <span className="text-xs" style={{ color: '#6B5D4F' }}>{(draft.vat_rate * 100).toFixed(1)}%</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: BRAND_BROWN }}>Lieferzeit (Tage)</label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min="1"
                value={draft.delivery_days_min}
                onChange={(e) => update({ delivery_days_min: e.target.value })}
                className="rounded-none w-16"
              />
              <span className="text-xs">–</span>
              <Input
                type="number"
                min="1"
                value={draft.delivery_days_max}
                onChange={(e) => update({ delivery_days_max: e.target.value })}
                className="rounded-none w-16"
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: BRAND_BROWN }}>Steuer-Hinweis</label>
          <Textarea
            value={draft.tax_note || ''}
            onChange={(e) => update({ tax_note: e.target.value })}
            className="rounded-none text-xs"
            rows={2}
          />
        </div>

        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: BRAND_BROWN }}>Rechtlicher Hinweis</label>
          <Textarea
            value={draft.legal_note || ''}
            onChange={(e) => update({ legal_note: e.target.value })}
            className="rounded-none text-xs"
            rows={2}
          />
        </div>

        {/* Legal warning box */}
        {(zone.country_code === 'FR' || zone.country_code === 'AT') && !draft.is_active && (
          <div className="mb-3 p-3 text-xs" style={{ backgroundColor: '#FBF6EE', color: '#5D4E3C', borderLeft: '3px solid #C4A084' }}>
            <div className="flex gap-2 items-start">
              <Info className="h-4 w-4 shrink-0 mt-0.5" style={{ color: BRAND_TAN }} />
              <div>
                <strong>Vor Aktivierung {zone.country_name} prüfen:</strong> Ab 10.000 € EU-Fernverkaufs-Gesamtumsatz pro Jahr → OSS-Verfahren (One-Stop-Shop) beim Bundeszentralamt für Steuern anmelden. Rechnung muss dann {zone.country_code === 'FR' ? 'französische' : 'österreichische'} MwSt. ausweisen.
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="text-xs" style={{ color: dirty ? '#B7862E' : '#8B7355' }}>
            {dirty ? 'Ungespeicherte Änderungen' : 'Alles gespeichert'}
          </div>
          <Button
            size="sm"
            className="rounded-none text-white"
            style={{ backgroundColor: BRAND_BROWN, opacity: dirty ? 1 : 0.5 }}
            onClick={save}
            disabled={busy || !dirty}
            data-testid={`save-zone-${zone.country_code}`}
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ShippingZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/shipping-zones');
      setZones(res.data.zones || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zonen konnten nicht geladen werden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activeCount = zones.filter((z) => z.is_active).length;

  return (
    <div className="p-6" data-testid="shipping-zones-page">
      <div className="mb-6">
        <h1 className="text-2xl font-light" style={{ color: BRAND_BROWN, letterSpacing: '0.02em' }}>
          <Globe className="h-6 w-6 inline-block mr-2 -mt-1" style={{ color: BRAND_TAN }} />
          Versand-Zonen &amp; Auslandsverkauf
        </h1>
        <p className="mt-1 text-sm text-neutral-600 max-w-2xl">
          Alle Zonen sind vorkonfiguriert mit Versandkosten, MwSt-Sätzen und rechtlichen Hinweisen. Aktiviere eine Zone erst, wenn alle rechtlichen Voraussetzungen (OSS-Anmeldung, Zoll-Dokumente etc.) erfüllt sind.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: BRAND_BROWN }}>
          <span className="px-2 py-1 border" style={{ borderColor: BRAND_TAN }}>
            <Check className="h-3 w-3 inline mr-1" style={{ color: '#7BA98E' }} />
            {activeCount} von {zones.length} Zonen aktiv
          </span>
        </div>
      </div>

      {/* Legal reminder banner */}
      <div className="mb-6 p-4 border-2" style={{ borderColor: '#D89B7B', backgroundColor: '#FBF6EE' }} data-testid="legal-reminder">
        <div className="flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#B7862E' }} />
          <div className="text-xs leading-relaxed" style={{ color: '#5D4E3C' }}>
            <strong>Rechtliche Checkliste vor Aktivierung eines neuen Landes:</strong>
            <ol className="mt-2 space-y-1 list-decimal ml-4">
              <li><strong>EU-Länder (AT, FR):</strong> OSS-Verfahren beim BZSt anmelden, wenn EU-Fernverkaufs-Gesamtumsatz &gt; 10.000 €/Jahr. Andernfalls DE-MwSt. ausweisen.</li>
              <li><strong>Widerrufsrecht:</strong> Belehrung in Landessprache (empfohlen) oder EN.</li>
              <li><strong>Versanddienstleister:</strong> DHL Paket International bzw. Deutsche Post Warensendung International.</li>
              <li><strong>Rücksende-Adresse:</strong> deutsche Adresse ist ok; Rücksende-Kosten trägt der Kunde (Standard).</li>
            </ol>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 p-8 text-center">Versand-Zonen werden geladen…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {zones.map((z) => (
            <ZoneCard key={z.country_code} zone={z} onSave={load} />
          ))}
        </div>
      )}
    </div>
  );
}
